import type {EntityCardProps} from '@Components/ai-chat-window/components/entity-card/entity-card.types';
import type {EntityCardAction, EntityCardActionContext, RegisteredEntityCardAction, RegisteredEntityCardActionSpec} from './entity-card-action.types';
import {createRegistry} from '@Components/ai-chat-window/lib/create-registry';
import {eventEntityCardAction} from './event.action';
import {pdfEntityCardAction} from './pdf.action';
import {socialPostEntityCardAction} from './social-post.action';
import {designEntityCardAction} from './design.action';
import {templateEntityCardAction} from './template.action';

/**
 * Fuse a typed action's parsePayload + onClick into one payload-erased `run`, closing over
 * TPayload so a wrong parsePayload/onClick pairing is caught here at registration — no cast.
 */
function defineEntityCardAction<TPayload extends object>(action: EntityCardAction<TPayload>): RegisteredEntityCardAction {
  const cardActions = (action.cardActions ?? []).map((spec): RegisteredEntityCardActionSpec => {
    return {
      icon: spec.icon,
      ariaLabelKey: spec.ariaLabelKey,
      isSelected: spec.isSelected,
      run: (raw: Record<string, unknown>, ctx: EntityCardActionContext) => spec.run(action.parsePayload(raw), ctx),
    };
  });

  const {fetchRefreshedCard, pointedAtIdOf} = action;

  return {
    entityType: action.entityType,
    pointedAtIdOf: pointedAtIdOf === undefined ? undefined : (raw): string => pointedAtIdOf(action.parsePayload(raw)),
    cardActions,
    run: (raw, ctx) => action.onClick(action.parsePayload(raw), ctx),
    fetchRefreshedCard: fetchRefreshedCard === undefined ? undefined : (raw): Promise<EntityCardProps | null> => fetchRefreshedCard(action.parsePayload(raw)),
  };
}

const REGISTRY = createRegistry(
  [defineEntityCardAction(eventEntityCardAction), defineEntityCardAction(pdfEntityCardAction), defineEntityCardAction(socialPostEntityCardAction), defineEntityCardAction(designEntityCardAction), defineEntityCardAction(templateEntityCardAction)],
  (action) => action.entityType,
);

export const getEntityCardAction = (entityType: string | undefined): RegisteredEntityCardAction | undefined => {
  if (entityType === undefined) {
    return undefined;
  }
  return REGISTRY[entityType];
};
